// One-time, idempotent SERVER -> LOCAL seed + a narrow online self-heal for a seeded-but-empty
// or incomplete native database. Fetches the user's existing records via authenticated APIs,
// populates the encrypted DB inside a transaction, verifies the result, and only then marks the
// migration complete. Never deletes server data. Safe to retry (upsert by id).
//
// Original emptying trigger remains unconfirmed. This module fixes the confirmed failure to
// detect and recover from a seeded-but-empty/incomplete native database.
import { SCHEMA_VERSION } from "./db";
import { upsertMany, countRecords, listRecordIds, isSeeded, markSeeded, recordId } from "./repo";

// export key -> local table (the single authoritative seed mapping; reused by self-heal).
const EXPORT_MAP = {
  practices: "practices",
  agreements: "agreement_versions",
  work_entries: "work_entries",
  payments: "payments",
  adjustments: "adjustments",
  expenses: "expenses",
  reserves: "reserve_deposits",
  settings: "settings",
  accountant_profile: "accountant_profiles",
  files: "files",
};

// Derive stable server ids per LOCAL table from a validated snapshot, using the SAME identity
// semantics the store uses (recordId) and the SAME single-doc normalization applySnapshot uses.
// This is the single source of truth for the self-heal completeness check — it is derived from
// EXPORT_MAP (+ payment_reports) so it can never drift from what seeding actually writes.
function snapshotIdsByTable(data, reports, userId) {
  const out = {};
  for (const [exportKey, table] of Object.entries(EXPORT_MAP)) {
    let rows = data[exportKey] || [];
    if (table === "settings" || table === "accountant_profiles") rows = normalizeSingle(rows, userId);
    out[table] = rows.map(recordId).filter(Boolean);
  }
  out.payment_reports = (reports || []).map(recordId).filter(Boolean);
  return out;
}

const normalizeSingle = (rows, userId) =>
  (rows || []).map((r) => ({ ...r, id: r.id || userId, user_id: r.user_id || userId }));

// Validate the /account/export payload shape. Returns { ok, reason }. A collection may be a
// legitimately empty array, but a MISSING key or a non-array value is malformed/partial and must
// NOT be treated as "zero records" (which would wrongly mark the DB seeded).
function validateExport(data) {
  if (!data || typeof data !== "object") return { ok: false, reason: "no-body" };
  for (const key of Object.keys(EXPORT_MAP)) {
    if (!(key in data)) return { ok: false, reason: `missing:${key}` };
    if (!Array.isArray(data[key])) return { ok: false, reason: `malformed:${key}` };
  }
  return { ok: true };
}

// Fetch the full authoritative export + payment reports. Returns { data, reports } or throws.
async function fetchServerSnapshot(netApi) {
  const exportRes = await netApi.get("/account/export");
  const data = exportRes.data;
  const v = validateExport(data);
  if (!v.ok) throw new Error(`export-invalid:${v.reason}`);
  // payment_reports are not part of /account/export — fetch active + deleted explicitly.
  const [activeReports, deletedReports] = await Promise.all([
    netApi.get("/payment-reports").then((r) => (Array.isArray(r.data) ? r.data : null)),
    netApi.get("/payment-reports?deleted=true").then((r) => (Array.isArray(r.data) ? r.data : [])).catch(() => []),
  ]);
  if (activeReports === null) throw new Error("export-invalid:payment-reports");
  return { data, reports: [...activeReports, ...deletedReports] };
}

// Write a validated snapshot into the DB (upsert-only) + verify + mark seeded, transactionally.
async function applySnapshot(exec, userId, data, reports) {
  const expected = {};
  await exec.transaction(async () => {
    for (const [exportKey, table] of Object.entries(EXPORT_MAP)) {
      let rows = data[exportKey] || [];
      if (table === "settings" || table === "accountant_profiles") rows = normalizeSingle(rows, userId);
      await upsertMany(exec, table, rows);
      expected[table] = (expected[table] || 0) + rows.length;
    }
    await upsertMany(exec, "payment_reports", reports);
    expected.payment_reports = reports.length;

    // Verify: every table holds at least the number of records we just wrote (idempotent upserts
    // mean re-runs never duplicate; counts only ever grow toward the server truth).
    for (const [table, n] of Object.entries(expected)) {
      const have = await countRecords(exec, table, userId);
      if (have < n) throw new Error(`Seed verification failed for ${table}: have ${have} < expected ${n}`);
    }
    await markSeeded(exec, userId, SCHEMA_VERSION);
  });
  return expected;
}

// First-launch seed. Only runs when not yet seeded. A structurally valid export (even if all
// collections are legitimately empty for a brand-new account) marks the DB seeded exactly once;
// a missing/malformed/partial export throws BEFORE marking, so it is retried next launch.
export async function seedFromServer(exec, netApi, userId) {
  if (!userId) return { skipped: "no-user" };
  if (await isSeeded(exec, userId)) return { alreadySeeded: true };
  const { data, reports } = await fetchServerSnapshot(netApi); // throws on invalid -> not marked
  const counts = await applySnapshot(exec, userId, data, reports);
  return { seeded: true, counts };
}

// ONLINE self-heal for a seeded-but-incomplete DB. Fetches the authoritative server snapshot
// (read-only) and compares stable server record IDs against local record IDs — by MEMBERSHIP,
// never by counts (A,B,X vs A,B,C must be detected as incomplete). If ANY server id is missing
// locally, force a full, validated, upsert-only re-hydration keyed by stable server id. A
// legitimately empty account leaves the DB untouched (no re-seed loop). Recovery never deletes
// local orphans (X is left in place — orphan reconciliation is out of scope) and never mutates
// or uploads to the server.
export async function selfHealIfNeeded(exec, netApi, userId) {
  if (!userId) return { skipped: "no-user" };
  if (!(await isSeeded(exec, userId))) return { skipped: "not-seeded" };

  // Authoritative read-only snapshot. Malformed/partial export throws export-invalid:* and must
  // abort loudly (never treated as "empty"); a network/offline failure changes nothing.
  let snapshot;
  try {
    snapshot = await fetchServerSnapshot(netApi);
  } catch (e) {
    const msg = String(e && e.message);
    if (msg.startsWith("export-invalid:")) throw e; // malformed -> abort, DB untouched
    return { skipped: "offline-or-probe-failed" };   // offline/transient -> change nothing
  }
  const { data, reports } = snapshot;

  const serverIds = snapshotIdsByTable(data, reports, userId);
  const serverTotal = Object.values(serverIds).reduce((n, ids) => n + ids.length, 0);
  // Genuinely-empty account -> valid, nothing to heal, no loop.
  if (serverTotal === 0) return { healthy: true, serverEmpty: true };

  // ID-based completeness: any authoritative server id absent locally => incomplete.
  const missing = {};
  let anyMissing = false;
  for (const [table, ids] of Object.entries(serverIds)) {
    if (!ids.length) continue;
    const localIds = new Set(await listRecordIds(exec, table, userId));
    const miss = ids.filter((id) => !localIds.has(id));
    if (miss.length) { missing[table] = miss.length; anyMissing = true; }
  }
  // Every server id present locally (local may hold extra orphans) -> healthy, no full hydration.
  if (!anyMissing) return { healthy: true };

  // Some server records missing locally -> validated, upsert-only re-hydration (no local deletes).
  const counts = await applySnapshot(exec, userId, data, reports);
  return { healed: true, missing, counts };
}

export { validateExport, snapshotIdsByTable };
